import { Offcanvas, Spinner } from "react-bootstrap";
import { CartProductList } from "../../components";
import { formatCurrency } from "../../utilities";
import { transDate } from "../../helpers";
import { useLayout, useShipping } from "../../context";
import { useAuth, useOrderOption, useStore } from "../../hooks";
import { useCart } from "../../hooks";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { OrderType } from "../../enums";
import { useNavigate } from "react-router-dom";
import { UpdateCart } from "../../services";
import { Cart as CartIcon, Clock, Pencil } from "react-bootstrap-icons";
import type { OffcanvasProps } from "react-bootstrap";
import React from "react";
import Skeleton from "react-loading-skeleton";
import { CartRecommendation } from "../../components/cart/CartRecommendation";
import { Toast } from "../../components/Toast";

type Props = {
  show: boolean;
  handleClose: () => void;
};

const RightSidebar: React.FC<Props> = ({ show, handleClose }) => {
  const navigate = useNavigate();

  const { isLoggedIn, isGuest, customerId, customer, deviceId } = useAuth();
  const { storeId, selectedStore, setStoreGateways, utm } = useStore();
  const {
    cartId,
    cartQuantity,
    cartItems,
    isLoading: isCartLoading,
    validateCart,
    cartSalePrice: cartPrice,
    cartTax,
  } = useCart();
  const { orderOption, setOrderOption } = useOrderOption();
  const { isLocationSelected, shippingLocation } = useShipping();
  const { setShowCart, setShowOptionModal, setActiveModal } = useLayout();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [placement, setPlacement] =
    useState<OffcanvasProps["placement"]>("end");

  // Optimize: Setup media query listener once
  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    setPlacement(media.matches ? "bottom" : "end");

    const handleMediaChange = (e: MediaQueryListEvent) => {
      setPlacement(e.matches ? "bottom" : "end");
    };

    media.addEventListener("change", handleMediaChange);
    return () => media.removeEventListener("change", handleMediaChange);
  }, []);

  const hasUpdatedCart = useRef(true);

  const {
    type_id: orderTypeId,
    order_date: orderDate,
    order_time: orderTime,
  } = orderOption;

  // Optimize: Memoize order type label logic
  const orderTypeLabel = useMemo(() => {
    switch (orderTypeId) {
      case OrderType.DELIVERY:
        return "Deliver to";
      case OrderType.PICKUP:
        return "Pickup from";
      case OrderType.DINEIN:
        return "Dine In at";
      default:
        return "";
    }
  }, [orderTypeId]);

  // Optimize: Memoize display address logic
  const displayAddress = useMemo(() => {
    return orderTypeId === OrderType.DELIVERY
      ? shippingLocation?.formatted_address
      : selectedStore?.store_address;
  }, [
    orderTypeId,
    shippingLocation?.formatted_address,
    selectedStore?.store_address,
  ]);

  // Optimize: Memoize total calculation
  const cartTotal = useMemo(() => cartPrice + cartTax, [cartPrice, cartTax]);

  const updateCart = useCallback(async () => {
    try {
      setIsLoading(true);

      const doordashObj = {
        external_delivery_id: `cart-${cartId}-${Date.now()}`,
        pickup_address: selectedStore?.store_address,
        dropoff_address: shippingLocation?.formatted_address,
        dropoff_location: {
          lat: shippingLocation?.latitude,
          lng: shippingLocation?.longitude,
        },
        dropoff_phone_number: customer?.phone || "",
        order_value: cartPrice * 100,
      };

      const { status, message, data } = await UpdateCart({
        store_id: storeId,
        device_id: deviceId,
        customer_id: customerId,
        cart_id: cartId,
        cart: cartItems,
        order_option: { ...orderOption, utm_source: utm },
        shipping: shippingLocation,
        doordashObj,
        source: utm,
      });

      setIsLoading(false);

      if (status) {
        setStoreGateways(data.gateway);
        setOrderOption({
          ...orderOption,
          tip_status: data.order_option.tip_status,
          tip: data.order_option.tip,
          third_party_delivery_order:
            data.order_option.third_party_delivery_order ?? "0",
        });
        setShowCart(false);
        navigate("/checkout");
      } else {
        Toast.error(message);
      }
    } catch (error) {
      Toast.error("Error fetching data:");
      setIsLoading(false);
    }
  }, [
    storeId,
    deviceId,
    customerId,
    cartId,
    cartItems,
    orderOption,
    shippingLocation,
    selectedStore?.store_address,
    customer?.phone,
    cartPrice,
    utm,
    setStoreGateways,
    setOrderOption,
    setShowCart,
    navigate,
  ]);

  useEffect(() => {
    if (!show || cartQuantity === 0) return;

    validateCart();
  }, [show, orderTypeId, validateCart]);

  useEffect(() => {
    if (!cartId) return;

    if ((isLoggedIn || isGuest) && customer?.phone && !hasUpdatedCart.current) {
      hasUpdatedCart.current = true;
      updateCart();
    }
  }, [isLoggedIn, isGuest, cartId, customer?.phone, updateCart]);

  const handleCheckoutClick = useCallback(async () => {
    hasUpdatedCart.current = false;

    if (!cartId) setShowOptionModal(true);
    else if (!isLoggedIn && !isGuest) setActiveModal("login");
    else if (!customer.phone) setActiveModal("customer");
    else if (
      (orderOption.type_id == OrderType.DELIVERY &&
        !shippingLocation?.formatted_address) ||
      !orderOption.order_date
    ) {
      setShowOptionModal(true);
    } else {
      try {
        setIsLoading(true);
        // Wait for validation to complete
        await validateCart();
        await updateCart();
      } catch (error) {
        console.error("Validation failed:", error);
        Toast.error("Cart validation failed. Please try again.");
        setIsLoading(false);
      }
    }
  }, [
    cartId,
    isLoggedIn,
    isGuest,
    customer,
    orderOption,
    shippingLocation,
    setShowOptionModal,
    setActiveModal,
    updateCart,
    setIsLoading,
    validateCart,
  ]);

  return (
    <>
      <Offcanvas
        show={show}
        onHide={handleClose}
        placement={placement}
        className="cart-offcanvas"
      >
        <div className="cart-offcanvas-inner">
          <Offcanvas.Header className="cart-sidebar-header" closeButton>
            <Offcanvas.Title className="cart-sidebar-title">
              <span>YOUR ORDER</span>
              <small>
                {cartQuantity} {cartQuantity === 1 ? "item" : "items"}
              </small>
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="cart-sidebar-body">
            {/* {isCartLoading && <InfiniteLoader className="mb-3" />} */}
            <div className="delivery_to">
              {cartQuantity > 0 ? (
                <div className="delivery_to_main1">
                  {orderTypeId == OrderType.PICKUP ||
                  orderTypeId == OrderType.DINEIN ||
                  (orderTypeId == OrderType.DELIVERY && isLocationSelected) ? (
                    <div className="cart-sidebar-location">
                      <div className="order_type_edit_btn d-flex justify-content-between align-items-center">
                        <h3>{orderTypeLabel}</h3>
                        <button onClick={() => setShowOptionModal(true)}>
                          <Pencil size={16} />
                        </button>
                      </div>
                      <div className="delivery_to_box">
                        {/* <div className="delivery_here">Home</div> */}
                        <p>{displayAddress}</p>
                        {orderDate && (
                          <i>
                            <Clock size={20} className="me-2" />
                            {transDate(orderDate)} at {orderTime}
                          </i>
                        )}
                      </div>
                    </div>
                  ) : (
                    ""
                  )}

                  <div className="cart-sidebar-items">
                    <CartProductList cartItems={cartItems} editable={true} />
                  </div>
                </div>
              ) : (
                <div className="text-center mt-5">
                  <CartIcon size={24} className="text-danger" />
                  <p className="mt-2 text-muted">Your cart is empty!</p>
                </div>
              )}

              <CartRecommendation />
            </div>
          </Offcanvas.Body>

          {cartPrice > 0 && (
            <div className="offcanvas-footer d-block p-0">
              <div className="cart-subtotal-section p-3">
                {isCartLoading ? (
                  <Skeleton />
                ) : (
                  <div className="d-flex justify-content-between align-items-center mb-2 cart-taxfees">
                    <span>Subtotal</span>
                    <span>{formatCurrency(cartPrice)}</span>
                  </div>
                )}

                {isCartLoading ? (
                  <Skeleton />
                ) : (
                  <div className="d-flex justify-content-between align-items-center cart-taxfees">
                    <span>Tax & Fees</span>
                    <span>{formatCurrency(cartTax)}</span>
                  </div>
                )}
              </div>
              {isCartLoading ? (
                <div className="p-3 cart-total border-top-soft">
                  <Skeleton />
                </div>
              ) : (
                <div className="d-flex justify-content-between align-items-center p-3 cart-total border-top-soft">
                  <span>Total</span>
                  <span>{formatCurrency(cartTotal)}</span>
                </div>
              )}
              <div className="p-3 pt-0">
                <button
                  className="w-100 align-items-center"
                  onClick={handleCheckoutClick}
                  disabled={isLoading || isCartLoading}
                >
                  {isLoading || isCartLoading ? (
                    <Spinner
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center w-100">
                      {!cartId ? "PROCEED" : "CHECKOUT"}
                      <span className="mx-2">•</span>
                      {formatCurrency(cartTotal)}
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </Offcanvas>
    </>
  );
};

export default React.memo(RightSidebar);
